// 치과 「재내원 안내」 /clinic-recalls (2026-10-01 대표 승인, 플래그 clinicRecall OFF 출고)
// 기획서 docs/기획서_리콜알림_벤치마킹_2026-10-01.md §4-5: 6구간 탭, 기본 동작 버튼 0, 행 단위 [다음 시기 변경]·[안내 중단]만.
//   자유 문구·수동 발송·재발송·내보내기·복사·인쇄 없음(법무 — 내보내기 0). 환자명은 명부와 같은 범위(이름 그대로, 연락처 없음).
//   종류는 트리거 코드 라벨(치과 화면 전용) — 환자 화면·푸시에는 서버 고정 템플릿만 나간다.
// 서버 계약 docs/계약_리콜_서버_2026-10-01.md §B-3 listClinicRecalls(버킷별 상한 300, 열람마다 접속기록). 목록은 CF 응답만(클라이언트 Firestore 읽기 차단).
// 권한: 오너·직원 모두(서버 resolveClinicActor). 라우트 게이트는 App.js(치과 계정 && isClinicRecallOn).
import React, { useCallback, useEffect, useState } from 'react';
import { BellRing, RefreshCw, CalendarClock, XCircle } from 'lucide-react';
import PageHeader from '../common/PageHeader';
import { unifiedStyles as U } from '../../styles/Styles';
import { RECALL_RULE_NOTICE, BUCKETS, listClinicRecalls, cancelPatientRecall, describeRecallError, triggerLabel, stageLabel, statusLabel, replyLabel, resultLabel, fmtDay } from '../../services/clinicRecallApi';
import RecallScheduleModal from './RecallScheduleModal';
import RecallSendBlockers from './RecallSendBlockers';

// 상단 안내문 — 기획서 §4-2 발송 규칙. 문구 변경 시 법적보안 확인
const NOTICE = RECALL_RULE_NOTICE;   // 2026-10-08 알림 파트와 공용(clinicRecallApi)

const ACTIVE = ['scheduled', 'sent', 'snoozed'];
const fmtMs = (ms) => {
    if (!ms) return '';
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
};

function ClinicRecalls({ user }) {
    const [bucket, setBucket] = useState('this_week');
    const [data, setData] = useState({});        // bucket → { items, count, truncated, counts }
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState([]);    // 버킷별 실패를 따로 표시(한쪽 실패가 다른 쪽을 숨기지 않게)
    const [actionError, setActionError] = useState('');
    const [busyId, setBusyId] = useState('');
    const [modal, setModal] = useState(null);    // { patientId, patientName, trigger }

    // 7구간(10-17 수신 거부 추가)을 한 번에 읽는다(탭 건수 표시). 열람 1회 = 접속기록 7건(계약 B-3)
    const load = useCallback(async () => {
        setLoading(true); setErrors([]);
        const results = await Promise.allSettled(BUCKETS.map(b => listClinicRecalls(b.key)));
        const next = {}; const errs = [];
        results.forEach((r, i) => {
            const b = BUCKETS[i];
            if (r.status === 'fulfilled' && r.value?.ok) next[b.key] = r.value;
            // 수신 거부 구간은 서버(listClinicRecalls optout 버킷, 2026-10-17)가 아직 반영 전이면 invalid-argument — 원인을 그대로 알린다(조용한 실패 금지)
            else if (b.key === 'optout' && r.status === 'rejected' && r.reason?.code === 'functions/invalid-argument') {
                console.error('[재내원 안내] 수신 거부 구간 미지원 서버:', r.reason?.message || '');
                errs.push(`[${b.label}] 서버 업데이트 전이라 아직 볼 수 없습니다.`);
            }
            else errs.push(`[${b.label}] 불러오지 못했습니다: ${describeRecallError(r.status === 'rejected' ? r.reason : new Error('응답 오류'))}`);
        });
        setData(next); setErrors(errs); setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    // [안내 중단] — 활성 안내를 종료(expired/cancelled). 되돌릴 수 없음(다시 하려면 [다음 시기 변경]로 새로 등록)
    const cancelRow = async (row) => {
        if (busyId) return;
        if (!window.confirm(`${row.name || '(이름 없음)'} 환자의 「${triggerLabel(row.trigger)}」 안내를 중단할까요?\n남은 발송이 모두 취소되며, 다시 하려면 [다음 시기 변경]으로 새로 등록해야 합니다.`)) return;
        setBusyId(row.recallId); setActionError('');
        try {
            await cancelPatientRecall(row.pid, row.recallId);
            await load();
        } catch (e) {
            setActionError(`[${row.name || '(이름 없음)'}] 중단 처리 실패: ` + describeRecallError(e));
        } finally { setBusyId(''); }
    };

    const cur = data[bucket] || { items: [], count: 0, truncated: false, counts: {} };
    // 계약 B-3: 응답 최상위 clinicPhoneMissing — 치과 대표전화(users.businessPhone) 검증값 없음 → 발송 보류(no_contact). 버킷 하나라도 true면 표시
    const clinicPhoneMissing = Object.values(data).some(v => v?.clinicPhoneMissing === true);
    // 계약 B-3(5차): clinicNameMissing — 치과명(users.businessName/companyName) 없음 → no_sender 보류 / clinicNameBanned — 치과명 금지어(치료명 포함) → banned 보류
    const clinicNameMissing = Object.values(data).some(v => v?.clinicNameMissing === true);
    const clinicNameBanned = Object.values(data).some(v => v?.clinicNameBanned === true);
    const thStyle = { ...U.tableTh, padding: '10px 12px', textAlign: 'left' };
    const tdStyle = { ...U.tableTd, padding: '10px 12px', color: '#334155' };

    return (
        <div style={U.content}>
            <PageHeader icon={BellRing} title="재내원 안내" subtitle="치과 명의로 환자 앱에 보내는 점검 시기 안내 — 대상·회신 확인"
                actions={<button type="button" onClick={load} disabled={loading} style={U.buttonSecondary}><RefreshCw size={14} /> 새로고침</button>} />

            <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', color: '#3730a3', borderRadius: 8, padding: '10px 12px', fontSize: 13, lineHeight: 1.6, marginBottom: 12 }}>{NOTICE}</div>

            {/* 발송 보류 경고 3종 — 알림 파트와 공용 컴포넌트(2026-10-08 검토: 중복 문구 제거). 문구는 이전과 같다 */}
            <RecallSendBlockers blockers={[clinicPhoneMissing && 'phone', clinicNameMissing && 'name', clinicNameBanned && 'banned'].filter(Boolean)} />

            {errors.map((m, i) => (
                <div key={i} style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 8 }}>{m}</div>
            ))}
            {actionError && <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 8 }}>{actionError}</div>}

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                {BUCKETS.map(b => {
                    const active = bucket === b.key;
                    const n = data[b.key]?.count;
                    return (
                        <button key={b.key} type="button" onClick={() => setBucket(b.key)}
                            style={{ ...U.buttonSecondary, padding: '6px 12px', fontSize: 13, background: active ? '#4f46e5' : '#fff', color: active ? '#fff' : '#334155', borderColor: active ? '#4f46e5' : '#e2e8f0' }}>
                            {b.label}{typeof n === 'number' ? ` ${n}${data[b.key]?.truncated ? '+' : ''}` : ''}
                        </button>
                    );
                })}
            </div>

            {bucket === 'replied' && cur.counts && (
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>확인했어요 {cur.counts.ack || 0} · 나중에 {cur.counts.snooze || 0}</div>
            )}
            {bucket === 'no_device' && <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>앱을 설치하지 않은 환자에게는 아무것도 보내지 않습니다. 데스크에서 직접 안내해 주세요.</div>}
            {bucket === 'optout' && <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>환자가 앱에서 이 치과의 재내원 안내를 끈 건입니다. 남은 발송은 모두 취소되었습니다. 다시 받기는 환자가 앱에서 직접 켜야 합니다 — 치과가 대신 켤 수 없습니다.</div>}
            {bucket === 'no_consent' && <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>재내원 안내 미동의(또는 환자가 끔) 환자입니다. 동의는 환자가 앱에서 직접 켭니다 — 치과가 대신 켤 수 없습니다.</div>}

            {loading ? (
                <div style={{ ...U.emptyState, padding: '40px 0' }}>불러오는 중...</div>
            ) : cur.items.length === 0 ? (
                <div style={U.emptyState}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#334155', marginBottom: 6 }}>해당 구간에 환자가 없습니다</div>
                    <div>환자 상세 화면의 [다음 안내 시기]로 등록하면 여기에 표시됩니다.</div>
                </div>
            ) : (
                <div style={{ ...U.card, padding: 0, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={thStyle}>환자</th>
                                <th style={thStyle}>종류</th>
                                <th style={thStyle}>기준일</th>
                                <th style={thStyle}>예정일</th>
                                <th style={thStyle}>단계</th>
                                <th style={thStyle}>상태</th>
                                <th style={thStyle}>회신</th>
                                <th style={thStyle}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {cur.items.map(row => {
                                const active = ACTIVE.includes(row.status);
                                const busy = busyId === row.recallId;
                                return (
                                    <tr key={row.recallId}>
                                        <td style={{ ...tdStyle, fontWeight: 600, color: '#0f172a' }}>
                                            {row.name || '(이름 없음)'}
                                            {row.fcmRegistered === false && <span style={{ ...U.badge, background: '#f1f5f9', color: '#64748b', marginLeft: 6, fontSize: 11 }}>앱 미설치</span>}
                                        </td>
                                        <td style={tdStyle}>{triggerLabel(row.trigger)}</td>
                                        <td style={tdStyle}>{fmtDay(row.basisDay)}</td>
                                        <td style={tdStyle}>{fmtDay(row.dueDay)}</td>
                                        <td style={tdStyle}>
                                            {stageLabel(row)}
                                            {(row.sendCount || 0) > 0 && <div style={{ fontSize: 11, color: '#94a3b8' }}>보냄 {row.sendCount}회{row.lastSentAt ? ` · 마지막 ${fmtMs(row.lastSentAt)}` : ''}</div>}
                                            {row.lastResult && row.lastResult !== 'ok' && <div style={{ fontSize: 11, color: '#b45309' }}>{resultLabel(row.lastResult)}</div>}
                                        </td>
                                        <td style={tdStyle}>
                                            <span style={{ ...U.badge, background: active ? '#dbeafe' : row.status === 'acknowledged' ? '#d1fae5' : '#f1f5f9', color: active ? '#1e40af' : row.status === 'acknowledged' ? '#047857' : '#475569' }}>{statusLabel(row)}</span>
                                        </td>
                                        <td style={tdStyle}>
                                            {replyLabel(row)}
                                            {row.ackAt && <div style={{ fontSize: 11, color: '#94a3b8' }}>{fmtMs(row.ackAt)}</div>}
                                            {row.snoozeAt && !row.ackAt && <div style={{ fontSize: 11, color: '#94a3b8' }}>{fmtMs(row.snoozeAt)}</div>}
                                            {row.status === 'optout' && row.optoutAt && <div style={{ fontSize: 11, color: '#94a3b8' }}>{fmtMs(row.optoutAt)}</div>}
                                            {/* 수신 거부 뒤 환자가 앱에서 다시 켠 경우 — 서버 키(reOptedIn, clinic-recall.js 수신거부 행 — recallPref.enabled)가 true 일 때만. 키가 없으면 아무것도 그리지 않는다 */}
                                            {row.status === 'optout' && row.reOptedIn === true && <span style={{ ...U.badge, background: '#d1fae5', color: '#047857', fontSize: 11, marginTop: 2 }}>{'다시 켬'}</span>}
                                        </td>
                                        <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                                            {/* 수신 거부 건은 [다음 시기 변경] 없음 — 환자의 거부를 치과가 다시 등록으로 덮지 않게 */}
                                            {row.status !== 'optout' && (
                                                <button type="button" disabled={busy} onClick={() => setModal({ patientId: row.pid, patientName: row.name, trigger: row.trigger })}
                                                    style={{ ...U.buttonSecondary, padding: '3px 10px', fontSize: 12 }}><CalendarClock size={12} /> 다음 시기 변경</button>
                                            )}
                                            {active && (
                                                <button type="button" disabled={busy} onClick={() => cancelRow(row)}
                                                    style={{ ...U.buttonSecondary, padding: '3px 10px', fontSize: 12, marginLeft: 6, color: '#b91c1c', borderColor: '#fecaca', background: '#fff', opacity: busy ? 0.6 : 1 }}><XCircle size={12} /> {busy ? '처리 중...' : '안내 중단'}</button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {cur.truncated && <div style={{ fontSize: 12, color: '#94a3b8', padding: '8px 12px' }}>{bucket === 'optout' ? '최근 수신 거부 300건까지만 표시됩니다.' : '예정일이 빠른 300건까지만 표시됩니다.'}</div>}
                </div>
            )}

            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 12, lineHeight: 1.6 }}>
                이 목록은 열람할 때마다 접속기록(누가·언제·어느 환자)이 남습니다. 환자 화면에는 점검 종류가 표시되지 않으며, 치과가 보내는 문구를 바꾸거나 직접 발송할 수 없습니다.
            </div>

            {modal && (
                <RecallScheduleModal patientId={modal.patientId} patientName={modal.patientName} initialTrigger={modal.trigger}
                    onClose={() => setModal(null)} onSaved={load} />
            )}
        </div>
    );
}

export default ClinicRecalls;
